import React, { useEffect, useState } from 'react'
import { Row, FormInput, MasterDetailForm, Widget, FormSwitch, Button, Table, ColumnProps, FormSelect } from '@digital/ui'
import { Collapse, Icon } from 'antd';
import { useRecoilState, useRecoilValue } from 'recoil';
import { orcadoAnoParametroState, solicitacaoIdState, solicitacaoState } from '../../../atoms';
import { RecursosParaExecucaoSolicitacao, createRecursoParaExecucaoSolicitacao, deleteRecursosParaExecucaoSolicitacao, getListaProjetosRecursosParaExecucao, getRecursosParaExecucaoSolicitacao, updateRecursosParaExecucaoSolicitacao } from '@app/api/naoPrevistos/recursosParaExecucaoSolicitacao';
import { CurrencyInput3 } from '@app/components/CurrencyInput3';
import { updateSolicitacaoNaoPrevisto } from '@app/api/naoPrevistos/solicitacao';
import ptBR from '@app/i18n/ptBR'
import { getAllProjetoDefinicao } from '@app/api/definicaoProjeto';
import { EStatusSolicitacaoNaoPrevisto, ETipoNaoPrevisto } from '@app/enums/ENaoPrevisto';
import { getAllCrfProjetoDefinicao } from '@app/api/crf/crfDefinicaoProjeto';
import { getProjetosNaoPrevisto } from '@app/api/naoPrevistos/parametros';
import { Visibility } from '@app/components/Visibility';
import { currencyFormatSemAbreviacao } from '@app/helpers/numbers';

const { Panel } = Collapse;

interface IRecursosParaExecucaoProps {
  noCollapse?: boolean;
}

const RecursosParaExecucao = ({ noCollapse }: IRecursosParaExecucaoProps) => {
  const idSolicitacao = useRecoilValue(solicitacaoIdState);
  const orcadoAnoNaoPrevisto = useRecoilValue(orcadoAnoParametroState);
  const [solicitacao, setSolicitacao] = useRecoilState(solicitacaoState);

  const [list, setList] = useState<RecursosParaExecucaoSolicitacao[]>([])
  const [recursos, setRecursos] = useState<RecursosParaExecucaoSolicitacao[]>([])
  const [projetos, setProjetos] = useState<any[]>([])

  const onSwitch = (value: any, property: string) => {
    const values = { ...solicitacao, [property]: value }
    setSolicitacao(values)
    updateSolicitacaoNaoPrevisto(values).then(() => {
      setSolicitacao((prev) => (prev ? { ...prev, [property]: value } : prev))
    })
  }

  const getLista = () => {
    if (idSolicitacao === undefined) return
    getRecursosParaExecucaoSolicitacao(idSolicitacao).then((response) => {
      if (response) {
        const newRecursosList = response.map((item: any, index: number) => {
          return { ...item }
        })
        setList([...response])
        setRecursos([...newRecursosList])
      }
    })
  }

  useEffect(() => {
    getLista()
  }, [idSolicitacao])

  const solicitacaoId = idSolicitacao || solicitacao?.id

  useEffect(() => {
    if (!solicitacaoId || typeof solicitacaoId !== 'string') return

    const loadProjetos = async () => {
      try {
        let lista: any[] = []

        // 1) Tenta usar a API específica de recursos para execução
        try {
          const response = await getListaProjetosRecursosParaExecucao(solicitacaoId)
          const raw = Array.isArray(response) ? response : (response as any)?.data
          if (raw && Array.isArray(raw) && raw.length > 0) {
            lista = raw
          }
        } catch {
          // ignora, vamos tentar fallbacks
        }

        // 2) Fallback: usa projetos do ciclo do ano (definição de projeto) por tipo CNP/ONP
        if (!lista.length) {
          const ano = solicitacao?.ano || orcadoAnoNaoPrevisto.ano
          if (ano) {
            if (solicitacao?.tipo?.toLowerCase() === ETipoNaoPrevisto.CNP.toLowerCase()) {
              const responseDef = await getAllProjetoDefinicao(ano)
              if (responseDef && Array.isArray(responseDef) && responseDef.length > 0) {
                lista = responseDef
              }
            } else if (solicitacao?.tipo?.toLowerCase() === ETipoNaoPrevisto.ONP.toLowerCase()) {
              const responseCrf = await getAllCrfProjetoDefinicao(ano)
              if (responseCrf && Array.isArray(responseCrf) && responseCrf.length > 0) {
                lista = responseCrf
              }
            }
          }
        }

        // 3) Fallback extra: usa projetos de NaoPrevistoParametros/Projetos (por orçado ano)
        if (!lista.length && orcadoAnoNaoPrevisto.id) {
          const respProjetos = await getProjetosNaoPrevisto(orcadoAnoNaoPrevisto.id)
          const rawAlt = Array.isArray(respProjetos) ? respProjetos : (respProjetos as any)?.data
          if (rawAlt && Array.isArray(rawAlt)) {
            lista = rawAlt
          }
        }

        if (lista && Array.isArray(lista)) {
          const normalized = lista
            .map((p: any) => ({
              codigo: p.codigo ?? p.Codigo ?? '',
              nome: p.nome ?? p.Nome ?? '',
              numero: p.numero ?? p.Numero ?? '',
            }))
            .filter((p: any) => p.codigo || p.nome)

          setProjetos(normalized)
        } else {
          setProjetos([])
        }
      } catch {
        setProjetos([])
      }
    }

    loadProjetos()
  }, [solicitacaoId, solicitacao?.tipo, solicitacao?.ano, orcadoAnoNaoPrevisto.id, orcadoAnoNaoPrevisto.ano])


  const handleAdd = () => {
    if (idSolicitacao === undefined) return
    createRecursoParaExecucaoSolicitacao({ solicitacaoNaoPrevistoId: idSolicitacao }).then((response) => {
      if (response) {
        getLista()
      }
    })
  }

  const handleEdit = (item: any) => {
    const itemDefault = recursos.find((i) => i.id === item.id)
    if (itemDefault === undefined) return
    if (JSON.stringify(itemDefault) === JSON.stringify(item)) return

    updateRecursosParaExecucaoSolicitacao(item).then((response) => {
      if (response) {
        getLista()
      }
    })
  }

  const handleChangeCodProjeto = (item: RecursosParaExecucaoSolicitacao, value: string) => {
    item.codProjeto = value
    item.nomeProjeto = projetos.filter((i) => i.codigo === value)[0].nome
    item.numeroTotvs = projetos.filter((i) => i.codigo === value)[0].numero
    handleEdit(item)
  }

  const handleChangeNomeProjeto = (item: RecursosParaExecucaoSolicitacao, value: string) => {
    item.nomeProjeto = value
    item.codProjeto = projetos.filter((i) => i.nome === value)[0].codigo
    item.numeroTotvs = projetos.filter((i) => i.nome === value)[0].numero
    handleEdit(item)
  }

  const handleChangeNumeroTotvs = (item: RecursosParaExecucaoSolicitacao, value: string) => {
    const newList = list.map((i) => {
      if (i.id === item.id) {
        i.numeroTotvs = value
      }
      return i
    })
    setList(newList)
  }

  const handleRemove = (id: string) => {
    deleteRecursosParaExecucaoSolicitacao(id).then((response) => {
      if (response) {
        getLista()
      }
    })
  }

  const totalValorDisponibilizado = list.reduce((n, { valorDisponibilizado }) => n + (valorDisponibilizado || 0), 0)

  const columns: ColumnProps<RecursosParaExecucaoSolicitacao>[] = [
    {
      title: 'N',
      dataIndex: 'index',
      width: '5%',
    },
    {
      title: 'Cód. Projeto',
      dataIndex: 'codProjeto',
      width: '15%',
      render(text, record, index) {
        let codigos = projetos.flatMap((item) => item.codigo)
        codigos = [...new Set(codigos)]
        codigos = codigos.sort()
        return (
          <FormSelect
            name='codProjeto'
            span={12}
            value={record.codProjeto}
            onChange={e => handleChangeCodProjeto(record, e)}
            options={codigos.map((item) => ({ text: item, value: item }))}
            showSearch
            optionFilterProp="children"
            filterOption={(input, item) => {
              return item.props.children.props.id
                .toString()
                .toLowerCase()
                .includes(input.toLowerCase());
            }}
            disabled={solicitacao.statusId !== EStatusSolicitacaoNaoPrevisto.PreenchimentoDaSolicitacao}
          />
        )
      },
    },
    {
      title: 'Nome do Projeto',
      dataIndex: 'nomeProjeto',
      render(text, record, index) {
        let nomes = projetos.flatMap((item) => item.nome)
        nomes = [...new Set(nomes)]
        nomes = nomes.sort()
        return (
          <FormSelect
            name='nomeProjeto'
            span={12}
            value={record.nomeProjeto}
            onChange={e => handleChangeNomeProjeto(record, e)}
            options={nomes.map((item) => ({ text: item, value: item }))}
            showSearch
            optionFilterProp="children"
            filterOption={(input, item) => {
              return item.props.children.props.id
                .toString()
                .toLowerCase()
                .includes(input.toLowerCase());
            }}
            disabled={solicitacao.statusId !== EStatusSolicitacaoNaoPrevisto.PreenchimentoDaSolicitacao}
          />
        )
      },
    },
    {
      title: 'Nº TOTVS',
      dataIndex: 'numeroTotvs',
      width: '15%',
      render(text, record, index) {
        return (
          <FormInput
            name={`numeroTotvs`}
            span={12}
            value={record.numeroTotvs}
            onChange={e => handleChangeNumeroTotvs(record, e.target.value)}
            onBlur={() => handleEdit(record)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleEdit(record)
              }
            }}
            disabled={solicitacao.statusId !== EStatusSolicitacaoNaoPrevisto.PreenchimentoDaSolicitacao}
          />
        )
      },
    },
    {
      title: 'Valor Disponibilizado',
      dataIndex: 'valorDisponibilizado',
      width: '20%',
      render(text, record, index) {
        return (
          <CurrencyInput3
            name={`valorDisponibilizado`}
            span={12}
            value={record.valorDisponibilizado}
            onChange={e => record.valorDisponibilizado = e}
            onBlur={() => handleEdit(record)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleEdit(record)
              }
            }}
            disabled={solicitacao.statusId !== EStatusSolicitacaoNaoPrevisto.PreenchimentoDaSolicitacao}
          />
        )
      },
    },
    {
      width: '5%',
      render: (item: any) =>
        <>
          <Visibility visible={solicitacao.statusId === EStatusSolicitacaoNaoPrevisto.PreenchimentoDaSolicitacao}>
            <Button
              confirm="Deseja realmente excluir este registro?"
              okText="Confirmar"
              icon="science-minus"
              onClick={() => handleRemove(item.id)}
            />
          </Visibility>
        </>
    },
  ]

  const content = (
          <MasterDetailForm
            initialValues={[]}
            key={''}>
            <Widget
              title=""
              actions={[]}>
              <Row>
                <FormSwitch
                  label={ptBR.lblRecursosParaExecucaoEconomiasEmOutrosProjetos}
                  name="recursosParaExecucaoEconomiasEmOutrosProjetos"
                  checked={true}
                  span={4}
                  onChange={value => onSwitch(value, 'recursosParaExecucaoEconomiasEmOutrosProjetos')}
                  disabled={true}
                />

                <FormInput
                  label='   '
                  name="mensagemRecursosParaExecucaoEconomiasEmOutrosProjetos"
                  span={8}
                  value='Para execução NÃO há a necessidade de dotação orçamentária, pois o valor será disponibilizado no(s) projeto(s) abaixo:'
                  disabled
                />
              </Row>

              <Row>
                <h4>Se houver recurso, informar o Projeto:</h4>
              </Row>

              <Row>
                <Visibility
                  visible={
                    solicitacao.statusId === EStatusSolicitacaoNaoPrevisto.PreenchimentoDaSolicitacao
                  }
                >
                  <Button style={{ float: 'inline-end' }} icon='commons-plus' type='link' onClick={handleAdd} />
                </Visibility>
              </Row>
              <Row>
                <Table
                  dataSource={list}
                  columns={columns}
                  rowKey="id"
                  pagination={false}
                />
              </Row>
              {list.length > 0 && (
                <Row>
                  <div style={{ marginTop: 8, textAlign: 'right', fontSize: 13, paddingRight: 8 }}>
                    <strong>{ptBR.lblTotalReais}:</strong> {currencyFormatSemAbreviacao(totalValorDisponibilizado)}
                    &nbsp;&nbsp;|&nbsp;&nbsp;
                    <strong>{ptBR.lblTotalDolar}:</strong> {currencyFormatSemAbreviacao(totalValorDisponibilizado / (orcadoAnoNaoPrevisto.valorDolar ?? 1))}
                  </div>
                </Row>
              )}

            </Widget>
          </MasterDetailForm>
  );

  if (noCollapse) return content;
  return (
    <>
      <Collapse bordered={false} defaultActiveKey={['2']} expandIcon={() => <Icon type="arrows-right_4" />}>
        <Panel key="2" header={ptBR.lblRecursosParaExecucaoEconomiasEmOutrosProjetos} style={{ border: 0 }}>
          {content}
        </Panel>
      </Collapse>
    </>
  );
}

export default RecursosParaExecucao